/**
 * The registration form: an email address and a password, and nothing else.
 *
 * No name field, no confirm-password field. A name we do not need is a field
 * that has to be validated, stored, exported and deleted; the worker derives a
 * display name from the address instead. Confirmation fields exist because
 * passwords are invisible — a reveal toggle solves the same problem without
 * making everyone type it twice.
 */

import { MIN_PASSWORD_LENGTH } from "@bigbear/shared";
import { type FormEvent, useState } from "react";

import { AuthError, type AuthApi } from "../../auth/authApi";
import { Field, firstMissing } from "./AuthCard";

interface Props {
  api: AuthApi;
  onRegistered: (email: string) => void | Promise<void>;
}

export function RegisterForm({ api, onRegistered }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    const missing = firstMissing([
      { label: "Email address", value: email },
      { label: "Password", value: password },
    ]);
    if (missing) {
      setError(missing);
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.register({ email, password });
      await onRegistered(email);
    } catch (cause) {
      setError(cause instanceof AuthError ? cause.message : "Could not reach the server. Please try again.");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      {error && (
        <p className="auth__error" role="alert">
          {error}
        </p>
      )}

      <Field
        id="email"
        label="Email address"
        type="email"
        value={email}
        autoComplete="email"
        onChange={setEmail}
        hint="We'll send a link here to confirm it's yours."
      />
      <Field
        id="password"
        label="Password"
        type="password"
        value={password}
        autoComplete="new-password"
        onChange={setPassword}
        hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
      />

      <button type="submit" className="button button--primary auth__submit" disabled={busy}>
        {busy ? "Creating your account…" : "Create account"}
      </button>
    </form>
  );
}
